import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface EditLessonFormProps {
  isOpen: boolean;
  onClose: () => void;
  onLessonUpdated: () => void;
  lessonId: number;
}

interface LessonFormData {
  title: string;
  summary: string;
  personal_notes: string;
  quiz: string;
  order: number;
  is_completed: boolean;
  video_file?: File | null;
  module?: number;
  transcript?: string;
  video_url?: string;
}

const EditLessonForm: React.FC<EditLessonFormProps> = ({ isOpen, onClose, onLessonUpdated, lessonId }) => {
  const [formData, setFormData] = useState<LessonFormData>({
    title: '',
    summary: '',
    personal_notes: '',
    quiz: '',
    order: 1,
    is_completed: false,
    video_file: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchLessonData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/lessons/${lessonId}/`);
      const lessonData = response.data;
      setFormData({
        title: lessonData.title,
        summary: lessonData.summary || '',
        personal_notes: lessonData.personal_notes || '',
        quiz: typeof lessonData.quiz === 'string' ? lessonData.quiz : '',
        order: lessonData.order,
        is_completed: lessonData.is_completed,
        video_file: null,
        module: lessonData.module?.id || lessonData.module,
        transcript: lessonData.transcript || '',
        video_url: lessonData.video_url || '',
      });
      setCurrentVideoUrl(lessonData.video_url || null);
    } catch (error) {
      console.error('Error fetching lesson:', error);
      setError('Failed to load lesson data');
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    if (isOpen && lessonId) {
      fetchLessonData();
    }
  }, [isOpen, lessonId, fetchLessonData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Create FormData to handle file upload
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('summary', formData.summary);
      formDataToSend.append('personal_notes', formData.personal_notes);

      // Quiz must be valid JSON or empty object
      const quizData = formData.quiz ? formData.quiz : '{}';
      formDataToSend.append('quiz', quizData);

      formDataToSend.append('order', formData.order.toString());
      formDataToSend.append('is_completed', formData.is_completed.toString());

      // Include required fields that shouldn't change
      if (formData.module) {
        formDataToSend.append('module', formData.module.toString());
      }
      if (formData.transcript !== undefined) {
        formDataToSend.append('transcript', formData.transcript);
      }
      // Only include video_url if it's a valid URL (not empty string)
      if (formData.video_url && formData.video_url.trim() !== '') {
        formDataToSend.append('video_url', formData.video_url);
      }

      // Only append video_file if a new one is selected
      if (formData.video_file) {
        formDataToSend.append('video_file', formData.video_file);
      }

      await apiClient.patch(`/api/lessons/${lessonId}/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onLessonUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error updating lesson:', error);
      setError('Failed to update lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, video_file: file }));
    }
  };

  const handleRemoveVideo = () => {
    setFormData(prev => ({ ...prev, video_file: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 10 }} onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(107, 114, 128, 0.75)',
            transition: 'opacity 0.2s'
          }} />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, zIndex: 10, overflowY: 'auto' }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center'
          }}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel style={{
                position: 'relative',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: RADIUS,
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all 0.2s',
                width: '100%',
                maxWidth: '32rem'
              }}>
                <div style={{ position: 'absolute', right: 0, top: 0, paddingRight: SPACING[16], paddingTop: SPACING[16] }}>
                  <button
                    type="button"
                    style={{
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.panel,
                      color: COLORS.text.tertiary,
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4]
                    }}
                    onClick={onClose}
                  >
                    <span style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      padding: 0,
                      margin: '-1px',
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      borderWidth: 0
                    }}>Close</span>
                    <XMarkIcon style={{ height: '24px', width: '24px' }} aria-hidden="true" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ marginTop: 0, textAlign: 'left', width: '100%' }}>
                    <Dialog.Title as="h3" style={{
                      fontSize: TYPOGRAPHY.headings.sizes.h2,
                      fontWeight: TYPOGRAPHY.weight.semibold,
                      lineHeight: '1.5',
                      color: COLORS.text.primary,
                      fontFamily: TYPOGRAPHY.headings.fontFamily
                    }}>
                      Edit Lesson
                    </Dialog.Title>

                    {error && (
                      <div style={{
                        marginTop: SPACING[8],
                        borderRadius: RADIUS,
                        backgroundColor: '#FEF2F2',
                        padding: SPACING[16]
                      }}>
                        <div style={{ fontSize: TYPOGRAPHY.body.sizes.default, color: COLORS.status.error }}>{error}</div>
                      </div>
                    )}

                    {loading ? (
                      <div style={{ marginTop: SPACING[16], display: 'flex', justifyContent: 'center' }}>
                        <div style={{
                          animation: 'spin 1s linear infinite',
                          borderRadius: '50%',
                          height: '32px',
                          width: '32px',
                          borderBottom: `2px solid ${COLORS.accent.default}`
                        }}></div>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} style={{ marginTop: SPACING[16], display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                        <div>
                          <label htmlFor="title" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            fontFamily: TYPOGRAPHY.body.fontFamily
                          }}>
                            Title
                          </label>
                          <input
                            type="text"
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              outline: 'none',
                              boxSizing: 'border-box'
                            }}
                            required
                          />
                        </div>

                        {/* Video Upload Section */}
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            fontFamily: TYPOGRAPHY.body.fontFamily
                          }}>Video</label>
                          <div style={{ marginTop: SPACING[4], display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
                            {currentVideoUrl && (
                              <div style={{ fontSize: TYPOGRAPHY.body.sizes.default, color: COLORS.text.secondary }}>
                                Current video: <a
                                  href={currentVideoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: COLORS.accent.default, textDecoration: 'none' }}
                                >View</a>
                              </div>
                            )}
                            <div style={{ flex: 1 }}>
                              <label
                                htmlFor="video_file"
                                style={{
                                  position: 'relative',
                                  cursor: 'pointer',
                                  borderRadius: RADIUS,
                                  backgroundColor: COLORS.bg.panel,
                                  fontWeight: TYPOGRAPHY.weight.medium,
                                  color: COLORS.accent.default
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: SPACING[8],
                                  padding: `${SPACING[8]} ${SPACING[12]}`,
                                  border: `1px solid ${COLORS.border.default}`,
                                  borderRadius: RADIUS
                                }}>
                                  <VideoCameraIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />
                                  <span>{formData.video_file ? formData.video_file.name : 'Upload new video'}</span>
                                </div>
                                <input
                                  id="video_file"
                                  name="video_file"
                                  type="file"
                                  accept="video/*"
                                  style={{
                                    position: 'absolute',
                                    width: '1px',
                                    height: '1px',
                                    padding: 0,
                                    margin: '-1px',
                                    overflow: 'hidden',
                                    clip: 'rect(0, 0, 0, 0)',
                                    whiteSpace: 'nowrap',
                                    borderWidth: 0
                                  }}
                                  onChange={handleFileChange}
                                  ref={fileInputRef}
                                />
                              </label>
                            </div>
                            {formData.video_file && (
                              <button
                                type="button"
                                onClick={handleRemoveVideo}
                                style={{
                                  fontSize: TYPOGRAPHY.body.sizes.default,
                                  color: COLORS.status.error,
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>

                        <div>
                          <label htmlFor="summary" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            fontFamily: TYPOGRAPHY.body.fontFamily
                          }}>
                            Summary
                          </label>
                          <textarea
                            id="summary"
                            value={formData.summary}
                            onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                            rows={3}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              outline: 'none',
                              resize: 'vertical',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>

                        <div>
                          <label htmlFor="personal_notes" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            fontFamily: TYPOGRAPHY.body.fontFamily
                          }}>
                            Personal Notes
                          </label>
                          <textarea
                            id="personal_notes"
                            value={formData.personal_notes}
                            onChange={(e) => setFormData({ ...formData, personal_notes: e.target.value })}
                            rows={3}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              outline: 'none',
                              resize: 'vertical',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>

                        <div>
                          <label htmlFor="quiz" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            fontFamily: TYPOGRAPHY.body.fontFamily
                          }}>
                            Quiz
                          </label>
                          <textarea
                            id="quiz"
                            value={formData.quiz}
                            onChange={(e) => setFormData({ ...formData, quiz: e.target.value })}
                            rows={3}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              outline: 'none',
                              resize: 'vertical',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>

                        <div>
                          <label htmlFor="order" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            fontFamily: TYPOGRAPHY.body.fontFamily
                          }}>
                            Order
                          </label>
                          <input
                            type="number"
                            id="order"
                            value={formData.order}
                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              outline: 'none',
                              boxSizing: 'border-box'
                            }}
                            min="1"
                            required
                          />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            id="is_completed"
                            checked={formData.is_completed}
                            onChange={(e) => setFormData({ ...formData, is_completed: e.target.checked })}
                            style={{
                              height: '16px',
                              width: '16px',
                              borderRadius: '4px',
                              border: `1px solid ${COLORS.border.default}`,
                              accentColor: COLORS.accent.default
                            }}
                          />
                          <label htmlFor="is_completed" style={{
                            marginLeft: SPACING[8],
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            color: COLORS.text.secondary,
                            fontFamily: TYPOGRAPHY.body.fontFamily
                          }}>
                            Mark as completed
                          </label>
                        </div>

                        <div style={{ marginTop: SPACING[24], display: 'flex', flexDirection: 'row-reverse', gap: SPACING[12] }}>
                          <button
                            type="submit"
                            disabled={loading}
                            style={{
                              display: 'inline-flex',
                              justifyContent: 'center',
                              borderRadius: RADIUS,
                              backgroundColor: COLORS.accent.default,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontWeight: TYPOGRAPHY.weight.semibold,
                              color: '#FFFFFF',
                              border: 'none',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              opacity: loading ? 0.5 : 1
                            }}
                          >
                            Save Changes
                          </button>
                          <button
                            type="button"
                            style={{
                              display: 'inline-flex',
                              justifyContent: 'center',
                              borderRadius: RADIUS,
                              backgroundColor: COLORS.bg.panel,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontWeight: TYPOGRAPHY.weight.semibold,
                              color: COLORS.text.primary,
                              border: `1px solid ${COLORS.border.default}`,
                              cursor: 'pointer'
                            }}
                            onClick={onClose}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditLessonForm;
